import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { IconButton, MenuItem, Select, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { DataGrid, SelectColumn } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ExportExcel from '../../Components/ExportExcel';
import {
  FilterContext,
  rowKeyGetter,
  FilterRenderer,
  inputStopPropagation,
  selectStopPropagation,
  autoFocusAndSelect,
  handleCopy,
  renderTextEditor,
  mergeEditedGridRows,
} from '../../Components/ReactDataGridProps';
import ManageReportDataApi from '../../Api/ApiManageReportData';


function OpportunityTable({
  name,
  rows,
  companies,
  organisations,
  orgLength,
  clients,
  projecttypes,
  projectsubtypes,
  employees,
  selectedCompany,
  selectedOrganisation,
  selectedDates,
  getStateData,
  onClickedAdd,
  onClickedDelete,
  resetReportingState,
  getClients,
  getEmployees,
  getProjectTypes,
  getProjectSubTypes,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [filters, setFilters] = useState({
    OpportunityId: '',
    Name: '',
    DimClientKey: 'All',
    DimEmployeeDirectoryKey: 'All',
    DimEmployeeProjectManagerKey: 'All',
    DimEmployeeSupervisorKey: 'All',
    DimEmployeeOpportunitySourceOneKey: 'All',
    DimEmployeeOpportunitySourceTwoKey: 'All',
    OpportunityType: 'All',
    OpportunitySubType: 'All',
    DimCompanyKey: 'All',
    DimOrganisationKey: 'All',
    enabled: false,
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortColumns, setSortColumns] = useState([]);
  const [frozenColumns, setFrozenColumns] = useState([]);

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'OpportunityId':
      case 'Name':
      case 'OpenedDate':
      case 'ClosedDate':
        return (a, b) => {
          return a[sortColumn]?.localeCompare(b[sortColumn]);
        };
  
      case 'DimCompanyKey':
        return (a, b) => {
          const aName = getCompanyNameById(a[sortColumn]);
          const bName = getCompanyNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimOrganisationKey':
        return (a, b) => {
          const aName = getOrganisationNameById(a[sortColumn]);
          const bName = getOrganisationNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimClientKey':
        return (a, b) => {
          const aName = getClientNameById(a[sortColumn]);
          const bName = getClientNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'DimEmployeeDirectoryKey':
      case 'DimEmployeeProjectManagerKey':
      case 'DimEmployeeSupervisorKey':
      case 'DimEmployeeOpportunitySourceOneKey':
      case 'DimEmployeeOpportunitySourceTwoKey':
        return (a, b) => {
          const aName = getEmployeeNameById(a[sortColumn]);
          const bName = getEmployeeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'OpportunityType':
        return (a, b) => {
          const aName = getOpportunityTypeNameById(a[sortColumn]);
          const bName = getOpportunityTypeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };

      case 'OpportunitySubType':
        return (a, b) => {
          const aName = getOpportunitySubTypeNameById(a[sortColumn]);
          const bName = getOpportunitySubTypeNameById(b[sortColumn]);
          
          return aName.localeCompare(bName);
        };
  
      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    setTableRows(rows);

    return () => {
      setTableRows([]);
    };
  }, [rows]);

  const getCompanyNameById = (id) => {
    if (!id) return '';
    const company = companies?.find(company => company.id === id);
    return company ? company.CompanyName : '';
  };

  const getOrganisationNameById = (id) => {
    if (!id) return '';
    const organisation = organisations?.find(organisation => organisation.id === id);
    return organisation ? organisation.OrgName : '';
  };

  const getClientNameById = (id) => {
    if (!id) return '';
    const client = clients?.find(client => client[0] === id);
    return client ? client[1] : '';
  };

  const getEmployeeNameById = (id) => {
    if (!id) return '';
    const employee = employees?.find(employee => employee[0] === id);
    return employee ? employee[1] : '';
  };

  const getOpportunityTypeNameById = (id) => {
    if (!id) return '';
    const opportunityType = projecttypes?.find(opportunityType => opportunityType[0] === id);
    return opportunityType ? opportunityType[1] : '';
  };
  
  const getOpportunitySubTypeNameById = (id) => {
    if (!id) return '';
    const opportunitySubType = projectsubtypes?.find(opportunitySubType => opportunitySubType[0] === id);
    return opportunitySubType ? opportunitySubType[1] : '';
  };

  const handleClickRow = (ids) => {
    setSelectedRow(null);
    setSelectedIds(ids);
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = [
      'DimCompanyKey',
      'DimOrganisationKey',
      'OpportunityId',
      'Name',
      'DimClientKey',
      'DimEmployeeDirectoryKey',
      'DimEmployeeProjectManagerKey',
      'DimEmployeeSupervisorKey',
      'DimEmployeeOpportunitySourceOneKey',
      'DimEmployeeOpportunitySourceTwoKey',
      'OpportunityType',
      'OpportunitySubType',
      'OpenedDate',
      'ClosedDate',
      'ReasonClosed',
    ];
    if (
      incompatibleColumns.includes(targetColumnKey) ||
      incompatibleColumns.includes(sourceColumnKey)
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const toggleFilters = () => {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  };

  const handleClickDelete = () => {
    if (selectedIds.size > 0) {
      onClickedDelete(name, Array.from(selectedIds));
      setSelectedIds(new Set());
      setSelectedRow(null);
    } else {
      if (selectedRow) {
        let rowIndexes = [selectedRow.id];
        onClickedDelete(name, rowIndexes);
        setSelectedRow(null);
      }
    }
  };

  const handleSelectFrozenColumns = (event, name) => {
    event.stopPropagation();
    event.preventDefault();

    const currentIndex = frozenColumns.indexOf(name);
    const newColumns = [...frozenColumns];

    if (currentIndex === -1) {
      newColumns.push(name);
    } else {
      newColumns.splice(currentIndex, 1);
    }
    setFrozenColumns(newColumns);
  };

  const handleExcelImport = (updated_data) => {
    // check import data is valid
    console.log('updated_data', updated_data);
    const validData = updated_data.every((item) => {
      return item.hasOwnProperty('OpportunityId') && item.hasOwnProperty('Name');
    });
    if (!validData) {
      toast.warning('Please insert valid data. Data must contain OpportunityId and Name.');
      return;
    }

    ManageReportDataApi.importDataFromExcel(
      name,
      updated_data,
      selectedDates,
      selectedCompany,
      selectedOrganisation,
    ).then((res) => {
      if (res.status === 200) {
        getClients();
        getEmployees();
        getProjectTypes();
        getProjectSubTypes();
        getStateData(res.data);
      } else toast.error(res.message);
    })
    .catch((error) => toast.error(error.message));
  };

  const handleGridRowsUpdated = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (updated_data.updated[field.column.key] === '') {
      toast.warning('Please insert the column value.');
      return;
    }


    if (selectedIds.size > 0) {
      updated_data.rowIds = [
        ...updated_data.rowIds,
        ...Array.from(selectedIds),
      ];      
    }

    updatedRows.map((row) => {
      if (updated_data.rowIds.includes(row.id))
        row[field.column.key] =
          updatedRows[field.indexes[0]][field.column.key];
      return row;
    });

    setTableRows((currentRows) =>
      mergeEditedGridRows(
        currentRows,
        updatedRows,
        updated_data.rowIds,
        updated_data.updated,
      ),
    );
    ManageReportDataApi.updateTemplate(name, updated_data)
      .then((res) => {
        if (res.status === 500) {
          toast.error(res.message);
        } else {
          resetReportingState();
        }
      })
      .catch((error) => toast.error(error.message));
  };

  const opportunityColumns = useMemo(() => {
    let opportunity_columns = [
      SelectColumn,
      {
        key: 'OpportunityId',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>ID</b>
            {frozenColumns.indexOf('OpportunityId') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpportunityId')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpportunityId')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'OpportunityId')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'OpportunityId',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 100,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.OpportunityId}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    OpportunityId: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('OpportunityId') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'Name',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Name</b>
            {frozenColumns.indexOf('Name') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Name')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Name')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'Name')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'Name')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.Name}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Name: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('Name') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
      {
        key: 'DimClientKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Client</b>
            {frozenColumns.indexOf('DimClientKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimClientKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimClientKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimClientKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 200,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimClientKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimClientKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimClientKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {clients &&
                  clients.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'block' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimClientKey ? props.row.DimClientKey : ''}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimClientKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {clients &&
                clients.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'block' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeDirectoryKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Director</b>
            {frozenColumns.indexOf('DimEmployeeDirectoryKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectoryKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeDirectoryKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 200,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeDirectoryKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeDirectoryKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeDirectoryKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeDirectoryKey
                  ? props.row.DimEmployeeDirectoryKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimEmployeeDirectoryKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeProjectManagerKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Project Manager</b>
            {frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeProjectManagerKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeProjectManagerKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeProjectManagerKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 200,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeProjectManagerKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeProjectManagerKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeProjectManagerKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeProjectManagerKey
                  ? props.row.DimEmployeeProjectManagerKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeProjectManagerKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeSupervisorKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Associate</b>
            {frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimEmployeeSupervisorKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 200,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeSupervisorKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeSupervisorKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeSupervisorKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeSupervisorKey
                  ? props.row.DimEmployeeSupervisorKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeSupervisorKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeOpportunitySourceOneKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 1</b>
            {frozenColumns.indexOf('DimEmployeeOpportunitySourceOneKey') !==
            -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceOneKey',
                  )
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceOneKey',
                  )
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeOpportunitySourceOneKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeOpportunitySourceOneKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 200,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeOpportunitySourceOneKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeOpportunitySourceOneKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeOpportunitySourceOneKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeOpportunitySourceOneKey
                  ? props.row.DimEmployeeOpportunitySourceOneKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeOpportunitySourceOneKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },
      {
        key: 'DimEmployeeOpportunitySourceTwoKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Source 2</b>
            {frozenColumns.indexOf('DimEmployeeOpportunitySourceTwoKey') !==
            -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceTwoKey',
                  )
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(
                    e,
                    'DimEmployeeOpportunitySourceTwoKey',
                  )
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimEmployeeOpportunitySourceTwoKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimEmployeeOpportunitySourceTwoKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 200,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimEmployeeOpportunitySourceTwoKey') !== -1
            ? true
            : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimEmployeeOpportunitySourceTwoKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimEmployeeOpportunitySourceTwoKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {employees &&
                  employees.map((cur) => (
                    <option
                      key={cur[0]}
                      value={cur[0]}
                      style={{ display: cur[2] ? 'display' : 'none' }}
                    >
                      {cur[1]}
                    </option>
                  ))}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimEmployeeOpportunitySourceTwoKey
                  ? props.row.DimEmployeeOpportunitySourceTwoKey
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    DimEmployeeOpportunitySourceTwoKey: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {employees &&
                employees.map((cur) => (
                  <MenuItem
                    key={cur[0]}
                    value={cur[0]}
                    style={{ display: cur[2] ? 'display' : 'none' }}
                  >
                    {cur[1]}
                  </MenuItem>
                ))}
            </Select>
          );
        },
      },      
      {
        key: 'Status',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Status</b>
            {frozenColumns.indexOf('Status') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Status')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'Status')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        width: 150,
        sortable: true,
        resizable: true,
        frozen: frozenColumns.indexOf('Status') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.Status}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    Status: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                <option value="Open">Open</option>
                <option value="Lost">Lost</option>
                <option value="Converted">Converted</option>
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.Status ? props.row.Status : ''}
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    Status: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Lost">Lost</MenuItem>
              <MenuItem value="Converted">Converted</MenuItem>
            </Select>
          );
        },
      },
      {
        key: 'PotentialRevenue',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Revenue</b>
            {frozenColumns.indexOf('PotentialRevenue') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'PotentialRevenue')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'PotentialRevenue')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'PotentialRevenue',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'PotentialRevenue',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 115,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.PotentialRevenue}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    PotentialRevenue: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('PotentialRevenue') !== -1 ? true : false,
        renderEditCell: (props) => (
          <input
            className="rdg-text-editor"
            ref={autoFocusAndSelect}
            value={props.row.PotentialRevenue}
            type="number"
            onChange={(event) =>
              props.onRowChange({
                ...props.row,
                PotentialRevenue: event.target.value,
              })
            }
            onBlur={() => props.onClose(true, false)}
          />
        ),
      },
      {
        key: 'ProbabilityOfSuccess',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Probability</b>
            {frozenColumns.indexOf('ProbabilityOfSuccess') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ProbabilityOfSuccess')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'ProbabilityOfSuccess')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find(
              (column) => column.columnKey === 'ProbabilityOfSuccess',
            )?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ProbabilityOfSuccess',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        frozen:
          frozenColumns.indexOf('ProbabilityOfSuccess') !== -1 ? true : false,
        width: 135,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.ProbabilityOfSuccess}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ProbabilityOfSuccess: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                <option value={0}>0</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
                <option value={50}>50</option>
                <option value={60}>60</option>
                <option value={70}>70</option>
                <option value={80}>80</option>
                <option value={90}>90</option>
                <option value={100}>100</option>
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.ProbabilityOfSuccess
                  ? parseInt(props.row.ProbabilityOfSuccess)
                  : ''
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    ProbabilityOfSuccess: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              <MenuItem value={0}>0</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={40}>40</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={60}>60</MenuItem>
              <MenuItem value={70}>70</MenuItem>
              <MenuItem value={80}>80</MenuItem>
              <MenuItem value={90}>90</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          );
        },
      },
      {
        key: 'WeightedPotentialRevenue',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Potential Revenue</b>
            {frozenColumns.indexOf('WeightedPotentialRevenue') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'WeightedPotentialRevenue')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'WeightedPotentialRevenue')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
          </div>
        ),
        resizable: true,
        frozen:
          frozenColumns.indexOf('WeightedPotentialRevenue') !== -1
            ? true
            : false,
        renderCell: (props) => {
          if (!props.row.PotentialRevenue || !props.row.ProbabilityOfSuccess)
            return <div></div>;
          return (
            <div style={{ marginTop: 5 }}>
              {(props.row.PotentialRevenue * props.row.ProbabilityOfSuccess) /
                100}
            </div>
          );
        },
      },
      {
        key: 'OpportunityType',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Type</b>
            {frozenColumns.indexOf('OpportunityType') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpportunityType')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpportunityType')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'OpportunityType')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'OpportunityType',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 150,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('OpportunityType') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.OpportunityType}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    OpportunityType: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {projecttypes &&
                  projecttypes.map(
                    (cur) =>
                      cur[2] && (
                        <option key={cur[0]} value={cur[0]}>
                          {cur[1]}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.OpportunityType != null && props.row.OpportunityType
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    OpportunityType: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projecttypes &&
                projecttypes.map(
                  (cur) =>
                    cur[2] && (
                      <MenuItem key={cur[0]} value={cur[0]}>
                        {cur[1]}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      },
      {
        key: 'OpportunitySubType',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>SubType</b>
            {frozenColumns.indexOf('OpportunitySubType') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'OpportunitySubType')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'OpportunitySubType')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 200,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('OpportunitySubType') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.OpportunitySubType}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    OpportunitySubType: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {projectsubtypes &&
                  projectsubtypes.map(
                    (cur) =>
                      cur[2] && (
                        <option key={cur[0]} value={cur[0]}>
                          {cur[1]}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.OpportunitySubType != null &&
                props.row.OpportunitySubType
              }
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    OpportunitySubType: e.target.value,
                  },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {projectsubtypes &&
                projectsubtypes.map(
                  (cur) =>
                    cur[2] && (
                      <MenuItem key={cur[0]} value={cur[0]}>
                        {cur[1]}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      },
      {
        key: 'OpenedDate',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Opened</b>
            {frozenColumns.indexOf('OpenedDate') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpenedDate')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'OpenedDate')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'OpenedDate')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'OpenedDate')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        sortable: true,
        frozen: frozenColumns.indexOf('OpenedDate') !== -1 ? true : false,
        renderCell: (props) => {
          return (
            <TextField
              defaultValue={
                props.row.OpenedDate != null && props.row.OpenedDate
              }
              variant="standard"
              key={props.row.id}
              type="date"
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    OpenedDate: e.target.value,
                  },
                  true,
                )
              }
            />
          );
        },
      },
      {
        key: 'ClosedDate',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Closed</b>
            {frozenColumns.indexOf('ClosedDate') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ClosedDate')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ClosedDate')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ClosedDate')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find((column) => column.columnKey === 'ClosedDate')
                ?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 175,
        sortable: true,
        frozen: frozenColumns.indexOf('ClosedDate') !== -1 ? true : false,
        renderCell: (props) => {
          return (
            <TextField
              defaultValue={
                props.row.ClosedDate != null && props.row.ClosedDate
              }
              variant="standard"
              key={props.row.id}
              type="date"
              onChange={(e) =>
                props.onRowChange(
                  {
                    ...props.row,
                    ClosedDate: e.target.value,
                  },
                  true,
                )
              }
            />
          );
        },
      },
      {
        key: 'ReasonClosed',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Reason Closed</b>
            {frozenColumns.indexOf('ReasonClosed') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ReasonClosed')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'ReasonClosed')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'ReasonClosed')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'ReasonClosed',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 160,
        sortable: true,
        resizable: true,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <input
                {...rest}
                className="filter-form"
                value={filters.ReasonClosed}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    ReasonClosed: e.target.value,
                  })
                }
                onKeyDown={inputStopPropagation}
              />
            )}
          </FilterRenderer>
        ),
        frozen: frozenColumns.indexOf('ReasonClosed') !== -1 ? true : false,
        renderEditCell: renderTextEditor,
      },
    ];

    if (orgLength.length > 1) {
      opportunity_columns.splice(1, 0, {
        key: 'DimOrganisationKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Department</b>
            {frozenColumns.indexOf('DimOrganisationKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) =>
                  handleSelectFrozenColumns(e, 'DimOrganisationKey')
                }
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimOrganisationKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimOrganisationKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen:
          frozenColumns.indexOf('DimOrganisationKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimOrganisationKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimOrganisationKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {organisations &&
                  organisations.map(
                    (cur) =>
                      p.row.DimCompanyKey === cur.DimCompanyKey &&
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.OrgName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={
                props.row.DimOrganisationKey != null &&
                props.row.DimOrganisationKey
              }
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimOrganisationKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {organisations &&
                organisations.map(
                  (cur) =>
                    props.row.DimCompanyKey === cur.DimCompanyKey &&
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.OrgName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    if (companies.length > 1) {
      opportunity_columns.splice(1, 0, {
        key: 'DimCompanyKey',
        name: (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <b style={{ marginRight: 5 }}>Company</b>
            {frozenColumns.indexOf('DimCompanyKey') !== -1 ? (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ fontSize: 20 }}
              />
            ) : (
              <VpnKeyIcon
                onClick={(e) => handleSelectFrozenColumns(e, 'DimCompanyKey')}
                style={{ opacity: 0.2, fontSize: 20 }}
              />
            )}
            {sortColumns.find((column) => column.columnKey === 'DimCompanyKey')
              ?.direction === 'ASC' ? (
              <ArrowDropUpIcon />
            ) : sortColumns.find(
                (column) => column.columnKey === 'DimCompanyKey',
              )?.direction === 'DESC' ? (
              <ArrowDropDownIcon />
            ) : null}
          </div>
        ),
        width: 205,
        resizable: true,
        sortable: true,
        frozen: frozenColumns.indexOf('DimCompanyKey') !== -1 ? true : false,
        headerCellClass: 'filter-cell',
        renderHeaderCell: (p) => (
          <FilterRenderer {...p}>
            {({ filters, ...rest }) => (
              <select
                {...rest}
                className="filter-form"
                value={filters.DimCompanyKey}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    DimCompanyKey: e.target.value,
                  })
                }
                onKeyDown={selectStopPropagation}
              >
                <option value="All">All</option>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <option key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </option>
                      ),
                  )}
              </select>
            )}
          </FilterRenderer>
        ),
        renderCell: (props) => {
          return (
            <Select
              fullWidth
              variant="standard"
              value={props.row.DimCompanyKey != null && props.row.DimCompanyKey}
              onChange={(e) =>
                props.onRowChange(
                  { ...props.row, DimCompanyKey: e.target.value },
                  true,
                )
              }
            >
              <MenuItem value={null}>None</MenuItem>
              {companies &&
                companies.map(
                  (cur) =>
                    cur.IsActive && (
                      <MenuItem key={cur.id} value={cur.id}>
                        {cur.CompanyName}
                      </MenuItem>
                    ),
                )}
            </Select>
          );
        },
      });
    }

    let frozenCols = opportunity_columns.filter((col) => col.frozen === true);
    let unfrozenCols = opportunity_columns.filter(
      (col) => col.frozen === false,
    );
    opportunity_columns = [...frozenCols, ...unfrozenCols].filter(
      (col) => !['Status', 'PotentialRevenue', 'ProbabilityOfSuccess', 'WeightedPotentialRevenue', 'ReasonClosed'].includes(col.key),
    );

    return opportunity_columns;
    // eslint-disable-next-line
  }, [
    tableRows,
    sortColumns,
    frozenColumns,
    companies,
    organisations,
    clients,
    employees,
    orgLength,
    projecttypes,
    projectsubtypes,
    handleSelectFrozenColumns,
  ]);

  const filteredRows = useMemo(() => {
    return tableRows?.filter((r) => {
      return (
        (filters.OpportunityId
          ? r.OpportunityId?.toLowerCase().includes(
              filters.OpportunityId.toLowerCase(),
            )
          : true) &&
        (filters.Name
          ? r.Name?.toLowerCase().includes(filters.Name.toLowerCase())
          : true) &&
        (filters.DimClientKey !== 'All'
          ? // eslint-disable-next-line
            r.DimClientKey == filters.DimClientKey
          : true) &&
        (filters.DimEmployeeDirectoryKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeDirectoryKey == filters.DimEmployeeDirectoryKey
          : true) &&
        (filters.DimEmployeeProjectManagerKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeProjectManagerKey ==
            filters.DimEmployeeProjectManagerKey
          : true) &&
        (filters.DimEmployeeSupervisorKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeSupervisorKey == filters.DimEmployeeSupervisorKey
          : true) &&
        (filters.DimEmployeeOpportunitySourceOneKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeOpportunitySourceOneKey ==
            filters.DimEmployeeOpportunitySourceOneKey
          : true) &&
        (filters.DimEmployeeOpportunitySourceTwoKey !== 'All'
          ? // eslint-disable-next-line
            r.DimEmployeeOpportunitySourceTwoKey ==
            filters.DimEmployeeOpportunitySourceTwoKey
          : true) &&
        (filters.OpportunityType !== 'All'
          ? // eslint-disable-next-line
            r.OpportunityType == filters.OpportunityType
          : true) &&
        (filters.OpportunitySubType !== 'All'
          ? // eslint-disable-next-line
            r.OpportunitySubType == filters.OpportunitySubType
          : true) &&
        (filters.DimCompanyKey !== 'All'
          ? r.DimCompanyKey === filters.DimCompanyKey
          : true) &&
        (filters.DimOrganisationKey !== 'All'
          ? r.DimOrganisationKey === filters.DimOrganisationKey
          : true)
      );
    });
  }, [tableRows, filters]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const exportValue = useMemo(() => {
    let export_value = {
      Opportunity: {
        columns: [],
        data: [],
      },
      Company: {
        columns: [],
        data: [],
      },
      Organisation: {
        columns: [],
        data: [],
      },
      Client: {
        columns: [],
        data: [],
      },
      Employee: {
        columns: [],
        data: [],
      },
      'Opportunity Type': {
        columns: [],
        data: [],
      },
      'Opportunity Sub Type': {
        columns: [],
        data: [],
      },
    };

    export_value.Opportunity.columns = [
      { title: 'OpportunityId', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
      { title: 'Company', width: { wpx: 80 } },
      { title: 'Organisation', width: { wpx: 80 } },
      { title: 'Client', width: { wpx: 80 } },
      { title: 'Director', width: { wpx: 80 } },
      { title: 'Project Manager', width: { wpx: 80 } },
      { title: 'Supervisor', width: { wpx: 80 } },
      { title: 'Source One', width: { wpx: 80 } },
      { title: 'Source Two', width: { wpx: 80 } },
      { title: 'Opportunity Type', width: { wpx: 100 } },
      { title: 'Opportunity Sub Type', width: { wpx: 120 } },
      { title: 'Opened Date', width: { wpx: 90 } },
      { title: 'Closed Date', width: { wpx: 90 } },
    ];
    
    tableRows.forEach((data) => {
      export_value.Opportunity.data.push([
        { value: data.OpportunityId ? data.OpportunityId : '' },
        { value: data.Name ? data.Name : '' },
        { value: data.DimCompanyKey ? getCompanyNameById(data.DimCompanyKey) : '' },
        { value: data.DimOrganisationKey ? getOrganisationNameById(data.DimOrganisationKey) : '' },
        { value: data.DimClientKey ? getClientNameById(data.DimClientKey) : '' },
        {
          value: data.DimEmployeeDirectoryKey
            ? getEmployeeNameById(data.DimEmployeeDirectoryKey)
            : ''
        },
        {
          value: data.DimEmployeeProjectManagerKey
            ? getEmployeeNameById(data.DimEmployeeProjectManagerKey)
            : ''
        },
        {
          value: data.DimEmployeeSupervisorKey
            ? getEmployeeNameById(data.DimEmployeeSupervisorKey)
            : ''
        },
        {
          value: data.DimEmployeeOpportunitySourceOneKey
            ? getEmployeeNameById(data.DimEmployeeOpportunitySourceOneKey)
            : ''
        },
        {
          value: data.DimEmployeeOpportunitySourceTwoKey
            ? getEmployeeNameById(data.DimEmployeeOpportunitySourceTwoKey)
            : ''
        },
        {
          value: data.OpportunityType ? getOpportunityTypeNameById(data.OpportunityType) : '',
        },
        {
          value: data.OpportunitySubType ? getOpportunitySubTypeNameById(data.OpportunitySubType) : '',
        },
        { value: data.OpenedDate ? data.OpenedDate : '' },
        { value: data.ClosedDate ? data.ClosedDate : '' },
      ]);
    });

    export_value.Company.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'CompanyName', width: { wpx: 80 } },
    ];
    companies &&
      companies.forEach((cur) => {
        export_value.Company.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.CompanyName },
        ]);
      });

    export_value.Organisation.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'OrgName', width: { wpx: 80 } },
    ];
    organisations &&
      organisations.forEach((cur) => {
        export_value.Organisation.data.push([
          { value: cur.id, style: { numFmt: '0' } },
          { value: cur.OrgName },
        ]);
      });

    export_value.Client.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    clients &&
      clients.forEach((cur) => {
        export_value.Client.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value.Employee.columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];
    employees &&
      employees.forEach((cur) => {
        export_value.Employee.data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Opportunity Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 120 } },
    ];
    projecttypes &&
      projecttypes.forEach((cur) => {
        export_value['Opportunity Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    export_value['Opportunity Sub Type'].columns = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 120 } },
    ];
    projectsubtypes &&
      projectsubtypes.forEach((cur) => {
        export_value['Opportunity Sub Type'].data.push([
          { value: cur[0], style: { numFmt: '0' } },
          { value: cur[1] },
        ]);
      });

    return export_value;
  }, [tableRows, companies, organisations, clients, employees, projecttypes, projectsubtypes]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ flex: 1 }} />
        <ExportExcel filename="Opportunity" value={exportValue} />
        <IconButton aria-label="Add" onClick={() => onClickedAdd(name)}>
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Search" onClick={toggleFilters}>
          <SearchIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={handleClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          rowKeyGetter={rowKeyGetter}
          className={
            filters.enabled
              ? 'filter-container fill-grid rdg-light'
              : 'fill-grid rdg-light'
          }
          headerRowHeight={filters.enabled ? 70 : 50}
          columns={opportunityColumns}
          rows={sortedRows}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          selectedRows={selectedIds}
          onSelectedRowsChange={handleClickRow}
          onRowsChange={handleGridRowsUpdated}
          onCellClick={handleCellClick}
          onCopy={handleCopy}
          onPaste={handlePaste}
          rowHeight={50}
          enableVirtualization={true}
          style={{ maxHeight: window.innerHeight - 180 - (23 * (selectedDates.length - 1)) }}
        />
      </FilterContext.Provider>
    </div>
  );
}

OpportunityTable.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  organisations: PropTypes.array.isRequired,
  clients: PropTypes.array.isRequired,
  projecttypes: PropTypes.array.isRequired,
  projectsubtypes: PropTypes.array.isRequired,
  employees: PropTypes.array.isRequired,
  selectedDates: PropTypes.array.isRequired,
  orgLength: PropTypes.array.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  getEmployees: PropTypes.func.isRequired,
  getProjectTypes: PropTypes.func.isRequired,
  getProjectSubTypes: PropTypes.func.isRequired,
};

export default OpportunityTable;
